import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Stack,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import OHIFViewer from '../../components/OHIFViewer';
import {
  getOrderDetailRequest,
  resetGetOrderDetail,
} from '../../redux/modules/externalReferrer/slice';

const ExternalImageWorkspace = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orderDetailRes, loading } = useSelector((state) => state.externalReferrer);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [pdfDialogUrl, setPdfDialogUrl] = useState('');
  const [pdfDialogTitle, setPdfDialogTitle] = useState('');
  const [pdfDialogType, setPdfDialogType] = useState('pdf');
  const [scImageUrls, setScImageUrls] = useState({});

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderDetailRequest(orderId));
    }
    return () => {
      dispatch(resetGetOrderDetail());
    };
  }, [orderId, dispatch]);

  const order = orderDetailRes?.data;
  const studyData = order?.studyData;
  const patientName = order?.patient?.name || 'Patient';

  const encapsulatedPDFs = useMemo(() => studyData?.encapsulatedPDFs || [], [studyData]);
  const dicomComments = useMemo(() => studyData?.dicomComments || null, [studyData]);
  const structuredReports = useMemo(() => studyData?.structuredReports || [], [studyData]);
  const secondaryCaptureInstances = useMemo(() => studyData?.secondaryCaptureInstances || [], [studyData]);

  const hasPdfs = encapsulatedPDFs.length > 0;
  const hasComments = !!(dicomComments?.studyComments || dicomComments?.imageComments?.length > 0);
  const hasSRs = structuredReports.length > 0;
  const hasSCs = secondaryCaptureInstances.length > 0;
  const hasWorksheetContent = hasPdfs || hasComments || hasSRs || hasSCs;

  const serverApiUrl = process.env.REACT_APP_SERVER_API || 'http://localhost:9000/';
  const ohifViewerUrl = process.env.REACT_APP_OHIF_VIEWER_URL || 'http://localhost:3001';
  const getPdfUrl = (instanceId) => `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/pdf`;
  const getPreviewUrl = (instanceId) => `${serverApiUrl}api/dicom/orthanc/instances/${instanceId}/rendered`;

  const getAuthHeaders = () => {
    const authToken = localStorage.getItem('item') || localStorage.getItem('token');
    const headers = {};
    if (authToken) headers.Authorization = authToken;
    return headers;
  };

  useEffect(() => {
    if (!hasSCs) return;

    const headers = getAuthHeaders();
    if (!headers.Authorization) return;

    let cancelled = false;
    let previousUrls = [];
    const objectUrls = [];

    const loadImages = async () => {
      const urls = {};
      for (const sc of secondaryCaptureInstances) {
        if (!sc.instanceId) continue;
        try {
          const res = await fetch(getPreviewUrl(sc.instanceId), { headers });
          if (!res.ok) continue;
          const blob = await res.blob();
          if (cancelled) return;
          const objectUrl = URL.createObjectURL(blob);
          objectUrls.push(objectUrl);
          urls[sc.instanceId] = objectUrl;
        } catch (e) {
          // skip failed image
        }
      }
      if (!cancelled) {
        setScImageUrls((prevUrls) => {
          previousUrls = Object.values(prevUrls);
          return urls;
        });
      }
    };

    loadImages();

    return () => {
      cancelled = true;
      previousUrls.forEach((url) => URL.revokeObjectURL(url));
      objectUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [secondaryCaptureInstances, hasSCs]);

  const handleDownloadPdf = async (instanceId, title) => {
    try {
      const response = await fetch(getPdfUrl(instanceId), { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to download PDF');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'worksheet'}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('PDF download error:', error);
    }
  };

  const handleOpenPdfDialog = async (instanceId, title) => {
    try {
      const response = await fetch(getPdfUrl(instanceId), { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to open PDF');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfDialogUrl(blobUrl);
      setPdfDialogType('pdf');
      setPdfDialogTitle(title || 'Worksheet');
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('PDF open error:', error);
    }
  };

  const handleOpenScDialog = async (instanceId, title) => {
    if (!instanceId) return;
    if (scImageUrls[instanceId]) {
      setPdfDialogUrl(scImageUrls[instanceId]);
      setPdfDialogType('image');
      setPdfDialogTitle(title || 'Measurement Image');
      setPdfDialogOpen(true);
      return;
    }

    try {
      const response = await fetch(getPreviewUrl(instanceId), { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to open image');
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfDialogUrl(blobUrl);
      setPdfDialogType('image');
      setPdfDialogTitle(title || 'Measurement Image');
      setPdfDialogOpen(true);
    } catch (error) {
      console.error('SC image open error:', error);
    }
  };

  const handleCloseDialog = () => {
    const cachedScUrls = new Set(Object.values(scImageUrls));
    const shouldRevoke = pdfDialogUrl?.startsWith('blob:') && !cachedScUrls.has(pdfDialogUrl);
    if (shouldRevoke) {
      URL.revokeObjectURL(pdfDialogUrl);
    }
    setPdfDialogOpen(false);
    setPdfDialogUrl('');
  };

  if (loading && !order) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/external')} sx={{ mb: 2 }}>
          Back to Orders
        </Button>
        <Alert severity="error">Order not found or access unavailable.</Alert>
      </Box>
    );
  }

  if (order.accessLevel !== 'report_and_images') {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/external/order/${orderId}`)} sx={{ mb: 2 }}>
          Back to Order
        </Button>
        <Alert severity="warning">Your access level is view-only for reports and does not include images.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 90px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/external/order/${orderId}`)} sx={{ mb: 0.5 }}>
            Back to Order
          </Button>
          <Typography variant="h5" fontWeight={700} color="primary.dark">
            Image Workspace
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {patientName} • {order.orderId}
          </Typography>
        </Box>
        {studyData?.orthancStudyInstanceUID && (
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={() => window.open(`${ohifViewerUrl}/viewer?StudyInstanceUIDs=${studyData.orthancStudyInstanceUID}`, '_blank')}
          >
            Open Viewer in New Tab
          </Button>
        )}
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, border: 1, borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#000' }}>
          <OHIFViewer
            studyInstanceUID={studyData?.orthancStudyInstanceUID}
            patientName={patientName}
            embedded={true}
          />
        </Box>

        {hasWorksheetContent && (
        <Paper sx={{ width: { xs: '100%', md: 420 }, borderRadius: 0, p: 2, overflow: 'auto', borderLeft: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon color="primary" fontSize="small" />
            Sonographer Worksheet
          </Typography>
          <Divider sx={{ my: 1.5 }} />

          {hasPdfs && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="primary" fontWeight={700}>
                PDFs ({encapsulatedPDFs.length})
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.75 }}>
                {encapsulatedPDFs.map((pdf, idx) => (
                  <Paper key={pdf.instanceId || idx} variant="outlined" sx={{ p: 1, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {pdf.documentTitle || `Worksheet ${idx + 1}`}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Open">
                        <IconButton size="small" onClick={() => handleOpenPdfDialog(pdf.instanceId, pdf.documentTitle || `Worksheet ${idx + 1}`)}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownloadPdf(pdf.instanceId, pdf.documentTitle)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {hasComments && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="primary" fontWeight={700}>
                Comments
              </Typography>
              <Paper variant="outlined" sx={{ p: 1, mt: 0.75 }}>
                {dicomComments?.studyComments && (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 0.5 }}>
                    {dicomComments.studyComments}
                  </Typography>
                )}
                {dicomComments?.imageComments?.map((c, i) => (
                  <Typography key={i} variant="body2">{c}</Typography>
                ))}
                {dicomComments?.performingPhysician && (
                  <Typography variant="caption" color="text.secondary">
                    Sonographer: {dicomComments.performingPhysician}
                  </Typography>
                )}
              </Paper>
            </Box>
          )}

          {hasSRs && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="primary" fontWeight={700}>
                Structured Reports ({structuredReports.length})
              </Typography>
              <Stack spacing={1} sx={{ mt: 0.75 }}>
                {structuredReports.map((sr, idx) => (
                  <Paper key={sr.instanceId || idx} variant="outlined" sx={{ p: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{sr.seriesDescription || `SR ${idx + 1}`}</Typography>
                    {sr.contentText && (
                      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                        {sr.contentText}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          {hasSCs && (
            <Box>
              <Typography variant="caption" color="primary" fontWeight={700}>
                Measurement Images ({secondaryCaptureInstances.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.75 }}>
                {secondaryCaptureInstances.map((sc, idx) => (
                  <Paper
                    key={sc.instanceId || idx}
                    variant="outlined"
                    sx={{ width: 120, overflow: 'hidden', cursor: 'pointer' }}
                    onClick={() => handleOpenScDialog(sc.instanceId, sc.imageComments || sc.seriesDescription || `Measurement ${idx + 1}`)}
                  >
                    {scImageUrls[sc.instanceId] ? (
                      <Box component="img" src={scImageUrls[sc.instanceId]} alt={`Measurement ${idx + 1}`} sx={{ width: '100%', height: 85, objectFit: 'cover' }} />
                    ) : (
                      <Box sx={{ width: '100%', height: 85, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <ImageIcon color="disabled" />
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            </Box>
          )}
        </Paper>
        )}
      </Box>

      <Dialog open={pdfDialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdfIcon sx={{ color: 'error.main' }} />
            <Typography variant="subtitle1" fontWeight={700}>{pdfDialogTitle}</Typography>
          </Box>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, bgcolor: 'grey.900', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {pdfDialogType === 'image' ? (
            <Box component="img" src={pdfDialogUrl} alt={pdfDialogTitle} sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          ) : (
            <iframe title={pdfDialogTitle} src={pdfDialogUrl} style={{ width: '100%', height: '100%', border: 'none' }} />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ExternalImageWorkspace;
